/* global globalThis */
import trace from '~/lib/trace';
import React, {useEffect, useState} from 'react';
import {View, TextInput} from 'react-native';
import moment from 'moment';

import sy from '~/styles';
import {
  user as userApi,
  service_request as serviceRequestApi,
} from '~/api/private';

import {
  Text,
  LessorPersona,
  Pressable,
  Menu,
  Dialog,
  Thumbnail,
  DealerPersona,
  Link,
} from '~/components/controls';

import MoreVertIcon from '~/images/md-icons/more_vert/materialicons/24px.svg';
import EditIcon from '~/images/md-icons/edit/materialicons/24px.svg';
import DeleteIcon from '~/images/md-icons/delete/materialicons/24px.svg';
import DoneIcon from '~/images/md-icons/done/materialicons/24px.svg';
import DoneAllIcon from '~/images/md-icons/done_all/materialicons/24px.svg';
import DocsIcon from '~/images/md-icons/docs/materialsymbolsoutlined/24px.svg';
import InfoIcon from '~/images/md-icons/info/materialsymbolsoutlined/24px.svg';
import PropTypes from 'prop-types';

const LINE_HEIGHT = 24;

const DOCUMENT_LOADING_HTML = `
  <div onclick="window.close();" style='position: absolute;background-color: #fff; padding: 4px;'><svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24" width="24"><path d="M0 0h24v24H0z" fill="none"/><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg></div>
  <div style='text-align: center; margin-top: 200px;'>Bezig met laden...</div>
`;

const DOCUMENT_ERROR_HTML = `
  <div onclick="window.close();" style='position: absolute;background-color: #fff; padding: 4px;'><svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24" width="24"><path d="M0 0h24v24H0z" fill="none"/><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg></div>
  <div style='text-align: center; margin-top: 200px;'>Document laden is mislukt. Probeer het opnieuw.</div>
`;

const DOCUMENT_VIEWER_HTML = `
  <div onclick="window.close();" style='position: absolute;background-color: #fff; padding: 4px;'><svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24" width="24"><path d="M0 0h24v24H0z" fill="none"/><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg></div>
  <iframe id="viewer" width="100%" height="100%" />
`;

const renderMessageStatus = ({
  onEdit,
  onDelete,
  dealer_name,
  is_read,
  is_sent,
}) => {
  if (onEdit || onDelete) {
    return (
      <Menu
        anchor={<InfoIcon width={14} height={14} fill="#FF6C00" />}
        contentStyle={{backgroundColor: '#6D6D6D'}}>
        <View style={[sy['px-2'], {width: 235}]}>
          <Text style={[sy.smallMedium, sy['text-white']]}>
            Dit bericht wordt verzonden bij het voorstellen van de opdracht
          </Text>
        </View>
      </Menu>
    );
  }

  if (!dealer_name) {
    return null;
  }

  if (is_read && is_sent) {
    return (
      <DoneAllIcon
        width={14}
        height={14}
        fill="#231fda"
        accessibilityLabel="Gelezen"
      />
    );
  }

  if (is_sent) {
    return (
      <DoneIcon
        width={14}
        height={14}
        fill="#231fda"
        accessibilityLabel="Verzonden"
      />
    );
  }

  return null;
};

const openAttachmentDocument = async ({request_id, message_id, index}) => {
  const openWindow = globalThis.window?.open ?? globalThis.open;
  const w = openWindow?.();

  if (!w) {
    return;
  }

  w.document.body.innerHTML = DOCUMENT_LOADING_HTML;

  const {result} =
    (await serviceRequestApi.attachment(request_id, message_id, index)) ?? {};

  if (!result) {
    w.document.body.innerHTML = DOCUMENT_ERROR_HTML;
    w.document.close();

    setTimeout(() => {
      w.close();
    }, 2000);
    return;
  }

  w.document.body.innerHTML = DOCUMENT_VIEWER_HTML;

  const script = w.document.createElement('script');
  script.text = `
    const byteArray = Uint8Array.from(atob('${
      result.split('base64,')[1]
    }'), c => c.charCodeAt(0));
    const blob = new Blob([byteArray], { type: 'application/pdf' });

    const viewer = document.getElementById('viewer');
    const objectUrl = URL.createObjectURL(blob);

    viewer.setAttribute('src', objectUrl);
    viewer.onload = () => URL.revokeObjectURL(objectUrl);
  `;

  w.document.body.appendChild(script);
  w.document.close();
};

const EditDialog = ({visible, content, onDismiss, onEdit}) => {
  const [value, setValue] = useState('');
  const [lines, setLines] = useState(1);

  useEffect(() => {
    setValue(content ?? '');
  }, [content]);

  return (
    <Dialog
      visible={visible}
      title="Bericht bewerken"
      onDismiss={onDismiss}
      buttons={[
        {
          text: 'Annuleren',
          onPress: onDismiss,
        },
        {
          text: 'Gereed',
          onPress: () => {
            onEdit(value);
            onDismiss();
          },
        },
      ]}>
      <View style={[sy['py-4']]}>
        <View
          style={[
            sy['flex-row'],
            sy['flex-1'],
            sy['p-2'],
            {
              borderRadius: 10,
              backgroundColor: '#EBEBEB',
            },
          ]}>
          <TextInput
            style={[
              sy.regular,
              sy['flex-1'],
              {
                color: value.length > 0 ? '#4A4A49' : '#828282',
                height: lines * LINE_HEIGHT,
              },
            ]}
            multiline
            placeholder={'Typ om het bericht te wijzigen'}
            value={value}
            onChangeText={setValue}
            onContentSizeChange={({nativeEvent}) => {
              const {height} = nativeEvent.contentSize;
              setLines(Math.ceil(height / LINE_HEIGHT));
            }}
          />
        </View>
      </View>
    </Dialog>
  );
};
EditDialog.propTypes = {
  visible: PropTypes.any,
  content: PropTypes.any,
  onDismiss: PropTypes.any,
  onEdit: PropTypes.any,
};

const Message = (props) => {
  const {
    style,
    content,
    attachments,
    timestamp: message_timestamp,
    message_id,
    request_id,
    user_id,
    dealer_id,
    dealer_name,
    fleet_owner_name,
    lessor,
    is_sent,
    onEdit,
    onDelete,
  } = props;
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState({});
  const [isRead, setIsRead] = useState(props.is_read);
  const [isMenuVisible, setIsMenuVisible] = useState(false);
  const [showEditMessageDialog, setShowEditMessageDialog] = useState(null);
  const [showDeleteMessageDialog, setShowDeleteMessageDialog] = useState(null);

  useEffect(() => {
    const touch = async () => {
      await serviceRequestApi.message_read(request_id, message_id);
      setIsRead(true);
    };

    if (message_id) {
      touch().catch(trace);
    }
  }, [message_id, request_id]);

  useEffect(() => {
    const fetch = async () => {
      const {first_name, last_name} = (await userApi.find(user_id)) ?? {};

      if (first_name && last_name) {
        setUser({
          initials: `${first_name[0]}${last_name[0]}`,
          name: `${first_name} ${last_name}`.trim(),
        });
      }

      setLoading(false);
    };

    if (user_id) {
      fetch().catch(trace);
    } else {
      setLoading(false);
    }
  }, [user_id]);

  let duration;
  if (message_timestamp) {
    const timestamp = moment(message_timestamp);
    if (timestamp.isSame(moment(), 'day')) {
      duration = timestamp.format('H:mm');
    } else {
      duration = timestamp.format('D MMMM YYYY');
    }
  }

  const dealer_style = [
    onEdit || onDelete ? sy['bg-tertiary-blue'] : sy['bg-tertiary-brown'],
    sy['ml-12'],
  ];

  const lessor_style = [sy['bg-white'], sy['mr-12']];

  let senderLabel = user.name;
  if (!senderLabel) {
    senderLabel = loading
      ? '\u00A0'
      : dealer_name ?? fleet_owner_name ?? lessor;
  }

  let messageHeaderPersona = (
    <LessorPersona lessor={fleet_owner_name ?? lessor} size={24} />
  );
  if (dealer_name) {
    messageHeaderPersona = <DealerPersona dealer_id={dealer_id} size={24} />;
  }

  return (
    <>
      <View
        style={[
          ...(dealer_name ? dealer_style : lessor_style),
          sy['overflow-hidden'],
          style,
        ]}>
        <View style={[sy['flex-1']]}>
          <View style={[sy['flex-1'], sy['flex-row'], sy['justify-between']]}>
            <View style={[sy['p-2']]}>
              <div data-private>
                <Text style={[sy.mediumBold, {lineHeight: 16}]}>
                  {senderLabel}
                </Text>
              </div>
            </View>
            {onEdit || onDelete ? (
              <Menu
                visible={isMenuVisible}
                anchor={<MoreVertIcon height={24} width={24} fill="#4a4a49" />}
                style={{marginTop: 24}}
                contentStyle={{backgroundColor: '#6D6D6D'}}
                onShow={() => setIsMenuVisible(true)}>
                <View style={[sy['px-2'], sy['flex-row'], sy['gap-4']]}>
                  {onEdit &&
                    !attachments?.length /* messages with attachments can't be edited */ && (
                      <Pressable
                        onPress={() => {
                          setShowEditMessageDialog(true);
                          setIsMenuVisible(false);
                        }}>
                        <EditIcon height={18} width={18} fill="#ffffff" />
                      </Pressable>
                    )}
                  {onDelete && (
                    <Pressable
                      onPress={() => {
                        setShowDeleteMessageDialog(true);
                        setIsMenuVisible(false);
                      }}>
                      <DeleteIcon height={18} width={18} fill="#ffffff" />
                    </Pressable>
                  )}
                </View>
              </Menu>
            ) : (
              messageHeaderPersona
            )}
          </View>
          <View style={[sy['px-2']]}>
            <Text style={sy.smallRegular}>{content}</Text>
            {attachments?.length > 0 && (
              <>
                {attachments?.map((item, index) => {
                  const attachmentKey =
                    item.id ??
                    `${message_id ?? ''}-${index}-${item.name ?? ''}`;

                  if (item.is_image) {
                    return (
                      <React.Fragment key={attachmentKey}>
                        <Thumbnail
                          request_id={request_id}
                          message_id={message_id}
                          index={index}
                          data={item.data}
                        />
                      </React.Fragment>
                    );
                  } else {
                    return (
                      <View
                        key={attachmentKey}
                        style={[
                          sy['flex-row'],
                          sy['items-center'],
                          sy['gap-2'],
                        ]}>
                        <View style={[sy['shrink-0']]}>
                          <DocsIcon width={24} height={24} fill="#4a4a49" />
                        </View>
                        <Link
                          onPress={async () => {
                            await openAttachmentDocument({
                              request_id,
                              message_id,
                              index,
                            });
                          }}
                          style={[sy['flex-1']]}
                          textStyle={[sy['truncate']]}>
                          {item.name.substring(0, item.name.indexOf('.'))}
                        </Link>
                      </View>
                    );
                  }
                })}
              </>
            )}
          </View>
        </View>
        <View
          style={[
            sy['flex-row'],
            sy['items-center'],
            sy['justify-end'],
            sy['gap-1'],
          ]}>
          <Text style={[sy.small, {lineHeight: 14}]}>{duration}</Text>
          {renderMessageStatus({
            onEdit,
            onDelete,
            dealer_name,
            is_read: isRead,
            is_sent,
          })}
        </View>
      </View>
      <EditDialog
        visible={showEditMessageDialog}
        content={content}
        onDismiss={() => setShowEditMessageDialog(false)}
        onEdit={(value) => onEdit(message_id, value)}
      />
      <Dialog
        visible={showDeleteMessageDialog}
        title="Bericht verwijderen?"
        buttons={[
          {
            text: 'Annuleren',
            onPress: () => setShowDeleteMessageDialog(false),
          },
          {
            text: 'Verwijderen',
            onPress: () => {
              onDelete(message_id);
              setShowDeleteMessageDialog(false);
            },
          },
        ]}>
        <View style={[sy['py-4']]}>
          <Text>Verwijderen kan niet ongedaan worden gemaakt.</Text>
        </View>
      </Dialog>
    </>
  );
};
Message.propTypes = {
  attachments: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      data: PropTypes.any,
      is_image: PropTypes.bool,
      name: PropTypes.string,
      request_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      message_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ),
  content: PropTypes.string,
  dealer_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  dealer_name: PropTypes.string,
  fleet_owner_name: PropTypes.string,
  is_read: PropTypes.bool,
  is_sent: PropTypes.bool,
  lessor: PropTypes.string,
  message_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onDelete: PropTypes.func,
  onEdit: PropTypes.func,
  request_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  timestamp: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  user_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  style: PropTypes.any,
};

export default Message;
